/**
 * Logger - Development logging utility
 * Provides environment-aware logging that can be disabled in production
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  prefix: string;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
};

class LoggerService {
  private config: LoggerConfig = {
    enabled: import.meta.env.DEV, // Only enabled in development mode
    level: 'debug',
    prefix: '[Flexiva]'
  };

  /**
   * Configure the logger
   */
  public configure(options: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...options };
  }

  /**
   * Enable or disable logging
   */
  public setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Set minimum log level
   */
  public setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  /**
   * Check if a log level should be output
   */
  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false;
    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.level];
  }

  /**
   * Format log message with prefix and context
   */
  private formatMessage(context: string, message: string): string {
    return `${this.config.prefix} [${context}] ${message}`;
  }

  /**
   * Debug level logging - for development tracing
   */
  public debug(context: string, message: string, ...args: unknown[]): void {
    if (this.shouldLog('debug')) {
      console.log(this.formatMessage(context, message), ...args);
    }
  }

  /**
   * Info level logging - for general information
   */
  public info(context: string, message: string, ...args: unknown[]): void {
    if (this.shouldLog('info')) {
      console.info(this.formatMessage(context, message), ...args);
    }
  }

  /**
   * Warning level logging - for potential issues
   */
  public warn(context: string, message: string, ...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage(context, message), ...args);
    }
  }

  /**
   * Error level logging - for errors (always logged unless disabled)
   */
  public error(context: string, message: string, ...args: unknown[]): void {
    if (this.shouldLog('error')) {
      console.error(this.formatMessage(context, message), ...args);
    }
  }

  /**
   * Create a scoped logger for a specific context
   */
  public scope(context: string): ScopedLogger {
    return new ScopedLogger(this, context);
  }
}

/**
 * Scoped logger for a specific context/module
 */
class ScopedLogger {
  constructor(
    private logger: LoggerService,
    private context: string
  ) {}

  public debug(message: string, ...args: unknown[]): void {
    this.logger.debug(this.context, message, ...args);
  }

  public info(message: string, ...args: unknown[]): void {
    this.logger.info(this.context, message, ...args);
  }

  public warn(message: string, ...args: unknown[]): void {
    this.logger.warn(this.context, message, ...args);
  }

  public error(message: string, ...args: unknown[]): void {
    this.logger.error(this.context, message, ...args);
  }
}

// Export singleton instance
export const Logger = new LoggerService();

// Export types for external use
export type { LogLevel, LoggerConfig, ScopedLogger };
